<template>
  <DpPanel>
    <template v-slot:header>
      <h1 class="text-center">{{ !noLicensing ? $t('registerPanel.join') : $t('registerPanel.createNewOnlineAccount') }}</h1>
      <p class="text-center" v-html="!noLicensing ? $t('registerPanel.joinInfo') : $t('registerPanel.createNewAccountInfo')"></p>

      <div
        class="alert"
        v-if="getBeforeFormAlert && getBeforeFormAlert.text"
        :class="[`text-${getBeforeFormAlert.color}`, `alert-${getBeforeFormAlert.backgroundColor}`]"
        v-html="getBeforeFormAlert.text"
      />

      <div class="sub-header">
        <p>
          {{ $t('registerPanel.alreadyHaveAccount') }}
          <b-link :href="loginLink" class="text-decoration-underline">{{ $t('login') }}</b-link>
        </p>
      </div>
    </template>

    <template v-slot:content>
      <b-row class="mb-2">
        <b-col>
          <ValidationObserver ref="form">
            <form @submit.prevent="validateSubmit">
              <DpBaseInput
                id="first-name-input"
                v-model.trim="form.firstName"
                name="first-name"
                type="text"
                input-autocomplete="off"
                validation-rules="required|min:2|allowedSpecialChar"
                class="sentry-mask"
                :label="$t('dp.input.firstName.label')"
                :placeholder="$t('dp.input.firstName.placeholder')"
              />
              <div class="mt-3">
                <DpBaseInput
                  id="last-name-input"
                  v-model.trim="form.lastName"
                  name="last-name"
                  type="text"
                  input-autocomplete="off"
                  validation-rules="required|min:2|allowedSpecialChar"
                  class="sentry-mask"
                  :label="$t('dp.input.lastName.label')"
                  :placeholder="$t('dp.input.lastName.placeholder')"
                />
              </div>
              <div class="mt-3">
                <DpBaseInput
                  id="email-input"
                  v-model.trim="form.email"
                  name="email"
                  type="email"
                  :label="$t('dp.input.email.label')"
                  :placeholder="$t('dp.input.email.placeholder')"
                  :validation-rules="getEmailRules"
                  vid="email"
                  input-autocomplete="off"
                />
              </div>
              <div class="mt-3">
                <DpBaseInput
                  id="password-input"
                  v-model.trim="form.password"
                  name="password"
                  type="password"
                  autocomplete="password"
                  use-password-validation-rules
                  class="sentry-mask"
                  :label="$t('dp.input.password.label')"
                  :placeholder="$t('dp.input.password.placeholder')"
                  :bails="false"
                  :vid="'password'"
                  additional-description="password-check-list"
                >
                  <template #default="{ failedRules, dirty, valid }">
                    <dp-secure-password-check-list id="password-check-list" :failed-rules="failedRules" :dirty="dirty" :valid="valid" />
                  </template>
                </DpBaseInput>
              </div>

              <b-row v-if="getNoticeOfCollectionContext" class="mb-3 mt-5">
                <b-col class="pr-0">
                  <b-card class="pb-0">
                    <h3 class="text-center">{{ getNoticeOfCollectionContext.title }}</h3>
                    <div v-html="getNoticeOfCollectionContext.body"></div>
                    <ValidationProvider
                      :rules="{ required: { allowFalse: false } }"
                      name="notice-of-collection-consent"
                      vid="notice-of-collection-consent"
                      v-slot="{ validated, valid, errors }"
                    >
                      <b-form-group :label-for="'notice-of-collection-consent'" class="mb-0">
                        <b-form-checkbox
                          id="notice-of-collection-consent"
                          class="mt-3 mb-1"
                          :state="(!validated || valid) && null"
                          v-model="noticeOfCollectionConsentInput"
                          required
                        >
                          <!-- eslint-disable-next-line vue/no-v-html -->
                          <span v-html="getNoticeOfCollectionContext.checkboxLabel"></span>
                        </b-form-checkbox>
                        <b-form-invalid-feedback
                          :state="(!validated || valid) && null"
                          :id="'notice-of-collection-consent-invalid-feedback'"
                        >
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </ValidationProvider>
                  </b-card>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="pr-0">
                  <ValidationProvider
                    :rules="{ required: { allowFalse: false } }"
                    name="terms-of-service-and-privacy-policy"
                    vid="terms-of-service-and-privacy-policy"
                    v-slot="{ validated, valid, errors }"
                  >
                    <b-form-group :label-for="'terms-of-service-and-privacy-policy'" class="mb-0">
                      <b-form-checkbox
                        id="terms-of-service-and-privacy-policy"
                        v-if="getTermsOfServiceAndPrivacyPolicyOptInCopy"
                        v-model="form.eula"
                        class="mt-3 mb-1"
                        :state="(!validated || valid) && null"
                        required
                      >
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span v-html="getTermsOfServiceAndPrivacyPolicyOptInCopy"></span>
                      </b-form-checkbox>
                      <b-form-invalid-feedback
                        :state="(!validated || valid) && null"
                        :id="'terms-of-service-and-privacy-policy-invalid-feedback'"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>
                </b-col>
                <b-col cols="1" class="pl-0">
                  <b-button
                    v-if="getTermsOfServiceAndPrivacyPolicyOptInCopyTooltip"
                    v-b-tooltip="{
                      title: getTermsOfServiceAndPrivacyPolicyOptInCopyTooltip,
                      placement: 'top',
                      trigger: 'hover click blur',
                      html: true,
                    }"
                    class="mt-3 ml-1 tooltip-btn"
                    :title="`${$t('dp.footer.tos')} and ${$t('dp.footer.privacy')} info`"
                  >
                    <fa-icon :icon="['fas', 'info-circle']" class="tooltip-info" />
                  </b-button>
                </b-col>
              </b-row>

              <b-form-checkbox v-if="getLostPetProgramOptInCopy" v-model="form.lostPetProgramOptIn" class="mb-4">
                {{ getLostPetProgramOptInCopy }}
              </b-form-checkbox>

              <b-form-checkbox v-if="getNationalPetRecordSearchOptInCopy" v-model="form.enableNetworkAnimalShelterSharing" class="mb-4">
                {{ getNationalPetRecordSearchOptInCopy }}
              </b-form-checkbox>

              <b-form-checkbox v-if="getMarketingCommunicationOptInCopy" v-model="form.marketingCommunicationOptIn" class="mb-4">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="getMarketingCommunicationOptInCopy"></span>
              </b-form-checkbox>

              <div class="form-errors" v-show="errorMessage" role="alert">
                <p class="p-0 m-0">
                  <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
                  {{ $t('errorMessages.error') }}
                </p>
                <ul>
                  <li>{{ errorMessage }}</li>
                </ul>
              </div>

              <b-button type="submit" class="breathing-room mt-3" variant="primary" block>
                {{ $t('continue') }}
              </b-button>
            </form>
          </ValidationObserver>
        </b-col>
      </b-row>
      <h2 class="text-primary m-0 p-0 mt-4 d-flex justify-content-center align-items-center h3">
        <b-img
          class="dp-logo-benefits mb-1 mr-2"
          fluid
          :src="require('@/assets/DocuPetLogo-Cyan.png')"
          :alt="$t('dp.navbar.logoDescription')"
        />
        <span>{{ $t('registerPanel.benefits.title') }}</span>
      </h2>
      <div id="account-benefits" class="my-3 card px-3 pt-3">
        <ul>
          <li
            v-for="(benefit, i) in Object.keys($t('registerPanel.benefits.items'))"
            :key="i"
            v-html="$t('registerPanel.benefits.items.' + benefit)"
          />
        </ul>
      </div>
    </template>
  </DpPanel>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { DpPanel, DpBaseInput, DpSecurePasswordCheckList, DpFocusableElementMixins } from '@docupet/component-library';
import i18n from '../../i18n';

const { mapActions: mapCityActions, mapGetters: mapCityGetters } = createNamespacedHelpers('city');

const { mapGetters, mapActions, mapState } = createNamespacedHelpers('register');

export default {
  name: 'Register',
  components: {
    DpPanel,
    DpBaseInput,
    DpSecurePasswordCheckList,
  },
  mixins: [DpFocusableElementMixins],
  props: {
    addPet: {
      type: Boolean,
      default: false,
    },
    context: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        city: this.$city,
        eula: false,
        marketingCommunicationOptIn: false,
        enableNetworkAnimalShelterSharing: false,
        lostPetProgramOptIn: false,
      },
      noticeOfCollectionConsentInput: false,
      errorMessage: '',
      emailExistsData: {
        exists: false,
        email: '',
        message: '',
      },
    };
  },
  methods: {
    ...mapActions(['register', 'getBreeds']),
    ...mapCityActions(['loadCityById']),
    async validateSubmit() {
      this.$refs.form.validate().then((success) => {
        if (success) {
          this.onSubmit();
        } else {
          this.scrollToError(this.$refs.form);
        }
      });
    },
    async onSubmit() {
      await this.register(this.form);

      if (this.success && this.petOwner) {
        window?.dataLayer?.push({ event: 'sign_up' });
        this.$router.push({
          name: 'AboutYourPet',
        });
      } else {
        // Display error messages
        let message = null;

        try {
          this.error.message.forEach((errMsg) => {
            let msg = '';
            if (errMsg.message === 'User email already exists') {
              msg = 'emailAlreadyExists';
            } else if (errMsg.message === 'Offline account already exists') {
              msg = 'accountAlreadyExists';
            }
            if (msg) {
              this.emailExistsData.exists = true;
              this.emailExistsData.email = this.form.email;
              if (msg === 'emailAlreadyExists')
                this.emailExistsData.message = i18n.t('dp.input.email.asyncErrorMessage.' + msg, { loginLink: this.loginLink });
              else {
                this.emailExistsData.message = i18n.t('dp.input.email.asyncErrorMessage.' + msg, { loginLink: this.activateCodeLink });
              }
            }
          });
        } catch (e) {
          message = i18n.t('errorMessages.genericError');
        }

        this.errorMessage = message;
      }
    },
  },
  computed: {
    ...mapCityGetters(['noLicensing']),
    ...mapGetters(['loading', 'success', 'error']),
    ...mapState({
      petOwner: (state) => state.data.petOwner,
      breeds: (state) => state.breeds,
      token: (state) => state.data.token,
      getEmailRules() {
        let ruleObject = { required: true, email: true };
        if (this.emailExistsData.exists) {
          ruleObject.emailExists = [this.emailExistsData.email, this.emailExistsData.message];
        }
        return ruleObject;
      },
    }),
    loginLink() {
      const redirect_path = this.$router.currentRoute?.query?.redirect_path;
      return `${window.BASE_URL}/login${redirect_path ? `?redirect_path=` + redirect_path : ''}`;
    },
    activateCodeLink() {
      return `${window.BASE_URL}/activate/code`;
    },
    getScreenContext() {
      return this.context;
    },
    getTermsOfServiceAndPrivacyPolicyOptInCopy() {
      return this.getScreenContext?.termsOfServiceAndPrivacyPolicyOptInCopy;
    },
    getTermsOfServiceAndPrivacyPolicyOptInCopyTooltip() {
      return this.getScreenContext?.termsOfServiceAndPrivacyPolicyOptInCopyTooltip;
    },
    getMarketingCommunicationOptInCopy() {
      return this.getScreenContext?.marketingCommunicationOptInCopy;
    },
    getNoticeOfCollectionContext() {
      return this.getScreenContext?.noticeOfCollectionConsentContext;
    },
    getBeforeFormAlert() {
      if (!this.getScreenContext?.beforeFormAlert) {
        return null;
      }

      return this.getScreenContext?.beforeFormAlert;
    },
    getNationalPetRecordSearchOptInCopy() {
      return this.getScreenContext?.nationalPetRecordSearchOptInCopy;
    },
    getLostPetProgramOptInCopy() {
      return this.getScreenContext?.lostPetProgramOptInCopy;
    },
  },
  watch: {
    addPet: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$router.push({
            name: 'AboutYourPet',
          });
        }
      },
    },
  },
  mounted() {
    this.loadCityById(this.$city);
  },
};
</script>
<style scoped>
ul {
  padding-left: 1.2em;
}
li {
  margin-bottom: 1em;
}

.dp-logo-benefits {
  width: 1.5em;
}
.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none;
}
</style>
